import { getRuntimeConfigValue } from '@/lib/runtime-config';
import { backofficeFetch } from '@/services/backoffice-api';
import { msalInstance } from '@/auth/msal';

export type PushNotificationStatus =
  'unsupported' | 'missing-key' | 'default' | 'denied' | 'enabled' | 'disabled';

export interface BackofficePushNotificationSettings {
  internalTesterNotifications: boolean;
}

const DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS: BackofficePushNotificationSettings = {
  internalTesterNotifications: false,
};
const BACKOFFICE_PUSH_NOTIFICATION_SETTINGS_STORAGE_PREFIX =
  'backoffice:push-notification-settings';

export function getPushNotificationStatus(subscription: PushSubscription | null) {
  if (!isPushNotificationSupported()) {
    return 'unsupported' satisfies PushNotificationStatus;
  }
  if (!getVapidPublicKey()) {
    return 'missing-key' satisfies PushNotificationStatus;
  }
  if (Notification.permission === 'denied') {
    return 'denied' satisfies PushNotificationStatus;
  }
  if (subscription) {
    return 'enabled' satisfies PushNotificationStatus;
  }
  if (Notification.permission === 'granted') {
    return 'disabled' satisfies PushNotificationStatus;
  }
  return 'default' satisfies PushNotificationStatus;
}

export function isPushNotificationSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export function getVapidPublicKey(): string | null {
  return getRuntimeConfigValue('VITE_BACKOFFICE_PUSH_VAPID_PUBLIC_KEY')?.trim() || null;
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.active) {
    return null;
  }

  return registration.pushManager.getSubscription();
}

export async function enableBackofficePushNotifications(): Promise<PushSubscription> {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported by this browser.');
  }

  const publicKey = getVapidPublicKey();
  if (!publicKey) {
    throw new Error('Backoffice push notifications are not configured.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const registration = await getReadyPushRegistration();
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription =
    existingSubscription ??
    (await subscribeToBrowserPush(registration, {
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await registerPushSubscription(subscription, getStoredBackofficePushNotificationSettings());
  return subscription;
}

export async function getPushNotificationFailureMessage(error: unknown): Promise<string> {
  const message =
    error instanceof Error ? error.message : 'Failed to enable browser notifications.';

  if (message.includes('Registration failed - push service error') && (await isBraveBrowser())) {
    return `${message}${message.endsWith('.') ? '' : '.'} In Brave, open brave://settings/privacy and enable "Use Google services for push messaging", then try again.`;
  }

  return message;
}

async function subscribeToBrowserPush(
  registration: ServiceWorkerRegistration,
  options: PushSubscriptionOptionsInit
): Promise<PushSubscription> {
  try {
    return await registration.pushManager.subscribe(options);
  } catch (error) {
    const name = error instanceof DOMException ? error.name : 'Error';
    const rawMessage = error instanceof Error ? error.message : String(error);
    const message = rawMessage.startsWith(`${name}: `)
      ? rawMessage.slice(`${name}: `.length)
      : rawMessage;
    throw new Error(`Browser push subscription failed (${name}): ${message}`);
  }
}

async function getReadyPushRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    await navigator.serviceWorker.register('/backoffice-push-sw.js');
  }

  return navigator.serviceWorker.ready;
}

async function isBraveBrowser(): Promise<boolean> {
  const brave = (
    navigator as Navigator & {
      brave?: { isBrave?: () => Promise<boolean> };
    }
  ).brave;

  try {
    return (await brave?.isBrave?.()) === true;
  } catch {
    return false;
  }
}

export async function disableBackofficePushNotifications(): Promise<void> {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return;
  }

  await deletePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
}

export async function getBackofficePushNotificationSettings(
  subscription?: PushSubscription | null
): Promise<BackofficePushNotificationSettings> {
  if (!subscription) {
    return getStoredBackofficePushNotificationSettings();
  }

  const response = await backofficeFetch('/push-notification-settings/current', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to load push notification settings (${response.status})`);
  }

  const body = (await response.json()) as {
    settings: BackofficePushNotificationSettings;
  };
  persistBackofficePushNotificationSettings(body.settings);
  return body.settings;
}

export async function updateBackofficePushNotificationSettings(
  settings: BackofficePushNotificationSettings,
  subscription: PushSubscription
): Promise<BackofficePushNotificationSettings> {
  const response = await backofficeFetch('/push-subscriptions', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      internalTesterNotifications: settings.internalTesterNotifications,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save push notification settings (${response.status})`);
  }

  const body = (await response.json()) as {
    settings: BackofficePushNotificationSettings;
  };
  persistBackofficePushNotificationSettings(body.settings);
  return body.settings;
}

export function getStoredBackofficePushNotificationSettings(): BackofficePushNotificationSettings {
  const storage = getLocalStorage();
  if (!storage) {
    return DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS;
  }

  const raw = storage.getItem(getBackofficePushNotificationSettingsStorageKey());
  if (!raw) {
    return DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<BackofficePushNotificationSettings>;
    return {
      ...DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS,
      ...parsed,
    };
  } catch {
    return DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS;
  }
}

function persistBackofficePushNotificationSettings(
  settings: BackofficePushNotificationSettings
): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  storage.setItem(getBackofficePushNotificationSettingsStorageKey(), JSON.stringify(settings));
}

function getBackofficePushNotificationSettingsStorageKey(): string {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  const accountKey = account?.homeAccountId ?? account?.username ?? 'anonymous';
  return `${BACKOFFICE_PUSH_NOTIFICATION_SETTINGS_STORAGE_PREFIX}:${accountKey}`;
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function registerPushSubscription(
  subscription: PushSubscription,
  settings: BackofficePushNotificationSettings
): Promise<void> {
  const response = await backofficeFetch('/push-subscriptions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      internalTesterNotifications: settings.internalTesterNotifications,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to register push subscription.');
  }

  const body = (await response.json()) as {
    settings?: BackofficePushNotificationSettings;
  };
  persistBackofficePushNotificationSettings(
    body.settings ?? DEFAULT_BACKOFFICE_PUSH_NOTIFICATION_SETTINGS
  );
}

async function deletePushSubscription(endpoint: string): Promise<void> {
  const response = await backofficeFetch('/push-subscriptions', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ endpoint }),
  });

  if (!response.ok) {
    throw new Error('Failed to disable push subscription.');
  }
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(rawData.length));

  for (let index = 0; index < rawData.length; index += 1) {
    output[index] = rawData.charCodeAt(index);
  }

  return output;
}
